"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import {
    discardHTMLTags,
    getCurrencySymbol,
    getFinalDiscountTag,
    getRandomStoreSeoTitle,
} from "@/constants/hooks";
import OfferOutUrl from "@/components/shared/OfferOutUrl";
import OfferDetailsToggle from "./OfferDetailsToggle";
import { BrandedProduct } from "@/services/dataTypes";
import { ArrowUpRight, ExternalLink, Sparkles } from "lucide-react";

interface Props {
    product: BrandedProduct;
    domain: string;
    imageSrc: string;
    merchantLogoSrc: string;
    merchantName: string;
    merchantHref: string;
    productUrl: string | null;
    index: number;
}

const BrandedProductCard = ({
    product,
    domain,
    imageSrc,
    merchantLogoSrc,
    merchantName,
    merchantHref,
    productUrl,
    index,
}: Props) => {
    const type = product?.offer_type?.name;

    const originalPrice = product?.original_price ? parseFloat(product.original_price) : 0;
    const salePrice = product?.sale_price ? parseFloat(product.sale_price) : 0;
    const discountPercent =
        originalPrice > 0 && salePrice > 0
            ? Math.round(((originalPrice - salePrice) / originalPrice) * 100)
            : null;

    const finalDiscountTag = getFinalDiscountTag(
        product?.offer_title,
        discountPercent,
    );

    return (
        <div className="group relative bg-white rounded-[2rem] border border-slate-100 flex flex-col h-full transition-all duration-500 hover:shadow-[0_30px_60px_-15px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 overflow-hidden">

            {/* ── Top Badges ── */}
            <div className="absolute top-3 left-3 right-3 z-10 flex justify-between items-start">
                {finalDiscountTag && (
                    <div className="bg-blue-600 text-white text-[10px] font-black px-3 py-1.5 rounded-full shadow-lg shadow-blue-100 uppercase tracking-widest flex items-center gap-1.5">
                        <Sparkles size={10} fill="white" />
                        {finalDiscountTag}
                    </div>
                )}
                {product?.coupon_code && (
                    <div className="bg-[#0F172A] text-white p-2 rounded-xl shadow-lg ml-auto">
                        <ArrowUpRight size={12} />
                    </div>
                )}
            </div>

            {/* ── Image Area ── */}
            <div className="relative aspect-[4/3] w-full p-8 overflow-hidden bg-slate-50/50">
                <div className="relative w-full h-full transform transition-transform duration-700 group-hover:scale-105 ease-out">
                    <Image
                        src={imageSrc}
                        alt={getRandomStoreSeoTitle(merchantName)}
                        fill
                        unoptimized
                        className="object-contain drop-shadow-md"
                        sizes="(max-width: 768px) 50vw, 25vw"
                        priority={index < 4}
                    />
                </div>
            </div>

            {/* ── Card Body ── */}
            <div className="p-6 flex flex-col flex-grow">

                {/* Merchant row */}
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-5 h-5 rounded-full bg-white border border-slate-100 flex items-center justify-center overflow-hidden shrink-0">
                        <Image
                            src={merchantLogoSrc}
                            alt={merchantName}
                            width={14}
                            height={14}
                            className="object-contain"
                            unoptimized
                        />
                    </div>
                    <Link
                        href={merchantHref}
                        className="text-[10px] font-bold text-slate-400 hover:text-blue-600 uppercase tracking-wider transition-colors truncate no-underline"
                    >
                        {merchantName}
                    </Link>
                </div>

                {/* Title */}
                <div className="mb-3">
                    <p className="text-base font-bold text-slate-900 leading-tight line-clamp-2 m-0">
                        {discardHTMLTags(product?.offer_title)}
                    </p>
                </div>

                {/* View Details — same logic as Theme-3: is_detail===1 → detail page, else modal */}
                <OfferDetailsToggle
                    domain={domain}
                    imageSrc={imageSrc}
                    merchantHref={merchantHref}
                    offer={product as any}
                    type="anchor"
                    isDetail={product?.is_detail}
                    productDetailUrl={productUrl}
                    buttonClass="text-slate-400 text-xs font-semibold hover:text-blue-500 transition mb-4 block"
                />

                {/* Price + expiry row */}
                <div className="mt-auto flex items-end justify-between pt-4 border-t border-slate-50">
                    <div>
                        {type === "product" ? (
                            <div className="flex flex-col">
                                {product?.original_price && originalPrice > 0 && salePrice > 0 && (
                                    <span className="text-[11px] text-slate-400 line-through">
                                        {getCurrencySymbol(product?.currency)}{product?.original_price}
                                    </span>
                                )}
                                {product?.sale_price && salePrice > 0 && (
                                    <span className="text-xl font-black text-slate-900">
                                        {getCurrencySymbol(product?.currency)}{product?.sale_price}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-100">
                                ACTIVE DEAL
                            </span>
                        )}
                    </div>

                    {/* Expiry */}
                    <div className="flex items-center gap-1 text-[10px] font-bold text-slate-400 shrink-0">
                        <svg className="w-3 h-3 text-slate-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                        </svg>
                        {product?.life_time === 0 && product?.end_date ? product.end_date : "Life Time"}
                    </div>
                </div>

                {/* CTA Button */}
                <div className="mt-5">
                    <OfferOutUrl
                        unique_id={product?.unique_id}
                        outUrl={product?.url}
                        merchantHref={merchantHref}
                        domain={domain}
                        customClass={`no-underline w-full py-3.5 rounded-xl font-bold text-[10px] uppercase tracking-widest flex items-center justify-center gap-2 transition-all duration-300 shadow-lg shadow-slate-100 border-none ${
                            product?.coupon_code
                                ? "bg-[#0F172A] hover:bg-[#D61C4E] !text-white"
                                : "bg-blue-600 hover:bg-[#0F172A] !text-white"
                        }`}
                    >
                        <span className="!text-white">
                            {product?.coupon_code ? "Show Code" : (type === "product" ? "Buy Now" : "Get Deal")}
                        </span>
                        {product?.coupon_code
                            ? <ArrowUpRight size={14} className="!text-white" />
                            : <ExternalLink size={14} className="!text-white" />
                        }
                    </OfferOutUrl>
                </div>
            </div>
        </div>
    );
};

export default BrandedProductCard;
